// battlePhase.ts - Battle/firing logic extracted from main.js

import { getFactions, getAbilities, Board, calculateScore, getStarRating } from './game.ts';
import type { GameState } from './game.ts';
import { FIRE_DEBOUNCE_MS, REMATCH_WINDOW_MS } from '../../shared/constants.ts';
import { escapeHtml, sanitizeIcon } from './utils.ts';
import { BugReport } from './bug-report.ts';
import type { AbilityResult } from '../../shared/protocol.ts';
import type { AbilityManager } from './abilities.ts';
import type { StandardRemoteDriver } from './drivers/standard.ts';
import type { renderer as rendererInstance } from './renderer.ts';
import { createAbilityRenderEnv, renderOwnAbilityResult, renderShotPassives } from './abilityRenderer.ts';
import type { abilityRenderer as abilityRendererInstance } from './abilityRenderer.ts';
import type { playerProfile as playerProfileInstance } from './playerProfile.ts';
import type { RoguelikeNode, RoguelikeRunState } from './roguelike.ts';
// Roguelike module is received lazily via ctx (set by main.js) to avoid
// a static import that would defeat code-splitting (above import is type-only).

type Renderer = typeof rendererInstance;
type AbilityRenderer = typeof abilityRendererInstance;
type PlayerProfile = typeof playerProfileInstance;
type RoguelikeModule = typeof import('./roguelike.ts');

// The node/run-state shapes now live in roguelike.ts (single source of
// truth). The *View aliases are kept for existing importers (main.ts,
// networkHandlers.ts).
export type RoguelikeNodeView = RoguelikeNode;
export type RoguelikeRunStateView = RoguelikeRunState;

/**
 * The slice of main.js's shared context that battlePhase actually reads.
 * First step of narrowing the shared context blob (Phase 4).
 */
export interface BattlePhaseContext {
    gameState: GameState;
    renderer: Renderer;
    abilityManager: AbilityManager;
    abilityRenderer: AbilityRenderer;
    playerProfile: PlayerProfile;
    getDriver: () => StandardRemoteDriver | null;
    handleUseAbility: (x: number, y: number) => Promise<boolean>;
    handleAbilityClick: (abilityId: string, immediate?: boolean) => void;
    clearSession: () => void;
    getRoguelike: () => RoguelikeModule;
    getRoguelikeRunState: () => RoguelikeRunStateView | null;
    getRoguelikeCurrentNode: () => RoguelikeNodeView | null;
}

let ctx: BattlePhaseContext;

// Sound manager
const sounds: Record<string, HTMLAudioElement | null> = {
    laser: document.getElementById('sound-laser') as HTMLAudioElement | null,
    explosion: document.getElementById('sound-explosion') as HTMLAudioElement | null,
    splash: document.getElementById('sound-splash') as HTMLAudioElement | null,
    destroyed: document.getElementById('sound-destroyed') as HTMLAudioElement | null,
    victory: document.getElementById('sound-victory') as HTMLAudioElement | null,
    defeat: document.getElementById('sound-defeat') as HTMLAudioElement | null
};

// Play sound effect (sounds are optional - won't error if not loaded)
export function playSound(name: string): void {
    const sound = sounds[name];
    if (sound && sound.src) {
        sound.currentTime = 0;
        sound.play().catch(() => {}); // Ignore autoplay errors
    }
}

// Game timer and turn counter
let gameTimerInterval: ReturnType<typeof setInterval> | null = null;
let gameStartTime: number | null = null;
let turnCount = 1;

function startGameTimer(): void {
    gameStartTime = Date.now();
    turnCount = 1;
    updateGameStats();

    gameTimerInterval = setInterval(() => {
        updateGameStats();
    }, 1000);
}

export function stopGameTimer(): void {
    if (gameTimerInterval) {
        clearInterval(gameTimerInterval);
        gameTimerInterval = null;
    }
}

function updateGameStats(): void {
    const timerEl = document.getElementById('game-timer');
    const turnCounterEl = document.getElementById('turn-counter');

    if (timerEl && gameStartTime) {
        const elapsed = Math.floor((Date.now() - gameStartTime) / 1000);
        const minutes = Math.floor(elapsed / 60);
        const seconds = elapsed % 60;
        timerEl.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    if (turnCounterEl) {
        turnCounterEl.textContent = String(turnCount);
    }
}

export function incrementTurnCount(): void {
    turnCount++;
    updateGameStats();
}

// Blitz mode - shot counter (exported: the server-SP path updates it from
// the shot-result handler in networkHandlers.ts)
export function updateBlitzCounter(): void {
    const { gameState } = ctx;
    const el = document.getElementById('blitz-shots-remaining');
    if (el) {
        el.textContent = String(gameState.stats.shotsFired);
    }
}

// Lock to prevent concurrent fire processing
let fireProcessing = false;
// Timestamp of last fire to prevent rapid clicks
let lastFireTime = 0;
// Safety timeout for multiplayer fire lock (cleared when server responds)
let fireProcessingTimeout: ReturnType<typeof setTimeout> | null = null;
const FIRE_LOCK_TIMEOUT_MS = 15000; // 15 seconds max wait for server response

export function clearFireProcessing(): void {
    fireProcessing = false;
    // The server answered, so the next click is a new shot, not a double
    // click: reset the debounce. Otherwise a fast round trip (blitz hands the
    // turn straight back) lands the player's next click inside the debounce
    // window and it is silently dropped.
    lastFireTime = 0;
    if (fireProcessingTimeout) {
        clearTimeout(fireProcessingTimeout);
        fireProcessingTimeout = null;
    }
}

// Rematch countdown state
let rematchCountdownInterval: ReturnType<typeof setInterval> | null = null;
let rematchCountdownEnd: number | null = null;

function startRematchCountdown(durationMs: number): void {
    stopRematchCountdown();
    rematchCountdownEnd = Date.now() + durationMs;
    updateRematchButtonText();
    rematchCountdownInterval = setInterval(updateRematchButtonText, 1000);
}

export function stopRematchCountdown(): void {
    if (rematchCountdownInterval) {
        clearInterval(rematchCountdownInterval);
        rematchCountdownInterval = null;
    }
    rematchCountdownEnd = null;
}

function updateRematchButtonText(): void {
    const btn = document.getElementById('btn-rematch') as HTMLButtonElement | null;
    if (!btn || btn.classList.contains('hidden')) {
        stopRematchCountdown();
        return;
    }
    const remaining = Math.max(0, Math.ceil((rematchCountdownEnd! - Date.now()) / 1000));
    const mins = Math.floor(remaining / 60);
    const secs = remaining % 60;
    const timeStr = `${mins}:${secs.toString().padStart(2, '0')}`;

    if (btn.disabled) {
        btn.textContent = `WAITING... (${timeStr})`;
    } else {
        btn.textContent = `REMATCH (${timeStr})`;
    }

    if (remaining <= 0) {
        stopRematchCountdown();
        btn.textContent = 'REMATCH EXPIRED';
        btn.disabled = true;
    }
}

export function setup(context: BattlePhaseContext): void {
    ctx = context;
}

// Start the battle phase
export function startBattle(): void {
    const { gameState, renderer, abilityRenderer } = ctx;

    gameState.gameStarted = true;
    gameState.startedAt = new Date().toISOString();

    // Roguelike campaign: record the battle as in progress before the first
    // shot, so a reload mid-battle counts as a loss on resume instead of
    // erasing the battle. (A same-page rejoin re-enters here with the same
    // node already pending — leave that record alone.)
    const runState = ctx.getRoguelikeRunState();
    const node = ctx.getRoguelikeCurrentNode();
    if (gameState.isRoguelike && gameState.mode === 'singleplayer' && runState && node
        && runState.pendingBattle?.nodeId !== node.id) {
        ctx.getRoguelike().markBattlePending(runState, node.id);
        ctx.getRoguelike().saveActiveRun(runState);
    }

    // Start game timer
    startGameTimer();

    // Set opponent faction colors for enemy board
    const factions = getFactions();
    const abilities = getAbilities();
    const playerFactionData = factions[gameState.faction!];
    const opponentFactionData = factions[gameState.opponentFaction!];
    if (opponentFactionData && opponentFactionData.colors) {
        renderer.setOpponentFactionStyle(opponentFactionData.colors);
    }

    // Set up battle screen with faction names
    renderer.updateBattleHeader(
        gameState.playerName || 'You',
        gameState.faction,
        gameState.opponentName,
        gameState.opponentFaction,
        opponentFactionData,
        abilities
    );

    // Update faction labels
    // Update board labels with player/faction info
    const yourFactionLabel = document.getElementById('your-faction-name');
    const enemyFactionLabel = document.getElementById('enemy-faction-name');
    const yourNameLabel = document.getElementById('your-name');
    const enemyNameLabel = document.getElementById('enemy-name');

    if (yourFactionLabel && playerFactionData) {
        yourFactionLabel.textContent = playerFactionData.name;
    }
    if (enemyFactionLabel && opponentFactionData) {
        enemyFactionLabel.textContent = opponentFactionData.name;
    }
    if (yourNameLabel) {
        yourNameLabel.textContent = gameState.playerName || 'You';
    }
    if (enemyNameLabel) {
        enemyNameLabel.textContent = gameState.opponentName || 'Enemy';
    }

    // Initialize enemy tracking board (preserve existing data for reconnection)
    if (!gameState.enemyBoard) {
        gameState.enemyBoard = new Board();
    }
    if (!gameState.enemyShots || gameState.enemyShots.size === 0) {
        gameState.enemyShots = new Map();
    }

    // Render boards
    renderer.renderYourBoard(gameState.playerBoard);
    renderer.renderEnemyBoard(gameState.enemyShots);

    // Render ship silhouettes (if in silhouette mode)
    renderer.renderShipSilhouettes(gameState.ships, renderer.yourBoard);

    // Render ship status
    renderer.renderShipsStatus(gameState.ships, 'your-ships-status');

    // Create ability panel under player's board section
    const yourShipsStatus = document.getElementById('your-ships-status')!;
    const yourSection = yourShipsStatus.parentNode as HTMLElement;
    let abilityPanelContainer = yourSection.querySelector('.ability-panel-container');
    if (!abilityPanelContainer) {
        abilityPanelContainer = document.createElement('div');
        abilityPanelContainer.className = 'ability-panel-container';
        yourSection.appendChild(abilityPanelContainer);
    }
    abilityPanelContainer.innerHTML = '';
    abilityRenderer.createAbilityPanel(abilityPanelContainer, ctx.handleAbilityClick);

    // Show/hide blitz shot counter
    const blitzCounter = document.getElementById('blitz-counter');
    if (blitzCounter) {
        if (gameState.isBlitz) {
            blitzCounter.classList.remove('hidden');
            updateBlitzCounter();
        } else {
            blitzCounter.classList.add('hidden');
        }
    }

    // Show/hide time trial badge
    const ttBadge = document.getElementById('time-trial-badge');
    if (ttBadge) {
        ttBadge.classList.toggle('hidden', !gameState.isTimeTrial);
    }
    // Highlight timer in time trial mode
    const timerItem = document.getElementById('timer-item');
    if (timerItem) {
        timerItem.classList.toggle('time-trial-active', gameState.isTimeTrial);
    }

    // All battles are server-driven (MP human or server-hosted AI room) —
    // board state and opponent tracking come from server events
    // (networkHandlers).

    // Create ship status tracking for the remote opponent (MP human or
    // server-hosted AI — the AI faction's ships come from the same data)
    if (opponentFactionData && opponentFactionData.ships) {
        gameState.opponentShipStatus = opponentFactionData.ships.map(s => ({
            class: s.class,
            sunk: false
        }));
        // Reconnection: re-apply enemy ships we had already sunk before the
        // refresh (stashed by restoreBattleState; server sends class + cells)
        if (gameState.pendingOpponentSunkShips?.length) {
            const enemyBoardEl = document.getElementById('enemy-board');
            for (const sunkShip of gameState.pendingOpponentSunkShips) {
                const entry = gameState.opponentShipStatus.find(s => s.class === sunkShip.shipClass && !s.sunk);
                if (entry) entry.sunk = true;
                for (const c of sunkShip.cells) {
                    const cellEl = enemyBoardEl?.querySelector(`[data-x="${c.x}"][data-y="${c.y}"]`);
                    if (cellEl) {
                        cellEl.classList.remove('hit');
                        cellEl.classList.add('sunk');
                    }
                }
            }
            gameState.pendingOpponentSunkShips = null;
        }
        renderer.renderShipsStatus(
            gameState.opponentShipStatus.map(s => ({ class: s.class, isSunk: () => s.sunk })),
            'enemy-ships-status'
        );
    }
    // Server-hosted SP roguelike battles still show the node's modifier
    // banner — the client keeps node.modifiers; only the battle is remote
    if (gameState.isRoguelike) {
        renderRoguelikeModifierBanner();
    }
    // Reset per-turn flags
    gameState.relentlessTriggeredThisTurn = false;
    renderer.updateTurnIndicator(gameState.isMyTurn);
    abilityRenderer.renderAbilities(gameState.isMyTurn);

    renderer.showScreen('battle-screen');
    BugReport.setCurrentScreen('battle');

    // Subtle pulse on bug report button to draw attention at match start
    BugReport.triggerPulse();
}

function renderRoguelikeModifierBanner(): void {
    const roguelikeCurrentNode = ctx.getRoguelikeCurrentNode();

    // Remove any existing banner
    const existing = document.querySelector('.roguelike-modifier-banner');
    if (existing) existing.remove();

    if (!roguelikeCurrentNode?.modifiers?.length) return;

    const battleHeader = document.querySelector('.battle-header');
    if (!battleHeader) return;

    const banner = document.createElement('div');
    banner.className = 'roguelike-modifier-banner';
    roguelikeCurrentNode.modifiers.forEach(mod => {
        const pill = document.createElement('span');
        pill.className = 'roguelike-modifier-pill';
        pill.innerHTML = `${sanitizeIcon(mod.icon)} ${escapeHtml(mod.name)}`;
        pill.title = mod.description;
        banner.appendChild(pill);
    });
    battleHeader.parentNode!.insertBefore(banner, battleHeader.nextSibling);
}

// Handle firing at enemy board
export async function handleFire(x: number, y: number): Promise<void> {
    const { gameState, abilityManager } = ctx;
    const driver = ctx.getDriver();

    // Block if already processing a shot, not our turn, or game over
    if (fireProcessing || !gameState.isMyTurn || gameState.gameOver || !driver) return;

    // Debounce rapid clicks
    const now = Date.now();
    if (now - lastFireTime < FIRE_DEBOUNCE_MS) return;
    lastFireTime = now;

    // Set lock IMMEDIATELY after passing guards, before any other checks
    fireProcessing = true;

    // Check if targeting with an ability
    if (abilityManager.isTargeting()) {
        await ctx.handleUseAbility(x, y);
        fireProcessing = false; // Clear lock after handling ability
        return;
    }

    // Check if already shot here
    if (gameState.enemyShots.has(`${x},${y}`)) {
        ctx.renderer.showToast('Already fired here!', true);
        fireProcessing = false; // Clear lock on validation failure
        return;
    }

    gameState.isMyTurn = false;
    // Text-only update (flipBoards=false): flipping the boards here and
    // back again when the server's turn-change lands flashed both boards
    // on every shot (always in blitz/extra-shot turns, where the turn
    // returns to the player immediately).
    ctx.renderer.updateTurnIndicator(false, 'WAITING...', false);
    ctx.abilityRenderer.renderAbilities(false);
    await driver.fire(x, y);

    // Note: fireProcessing is cleared when we receive shot-result or turn-change
    // Safety timeout: if server never responds, release the lock to prevent permanent lockout
    fireProcessingTimeout = setTimeout(() => {
        if (fireProcessing) {
            console.warn('Fire processing timeout — server did not respond within', FIRE_LOCK_TIMEOUT_MS, 'ms');
            fireProcessing = false;
            fireProcessingTimeout = null;
            // Restore turn state so player can retry
            if (ctx.gameState && !ctx.gameState.gameOver) {
                ctx.gameState.isMyTurn = true;
                ctx.renderer.updateTurnIndicator(true);
                ctx.renderer.showToast('Connection issue — please try again', true);
            }
        }
    }, FIRE_LOCK_TIMEOUT_MS);
}

// End the game
/** endGame options for results the server decided outside the battle itself. */
export interface EndGameOptions {
    /**
     * The opponent forfeited (left the game / failed to reconnect): the server
     * has already closed the room, so no rematch is possible, and `forfeitMessage`
     * replaces the "fleet destroyed" game-over message.
     */
    forfeit?: boolean;
    forfeitMessage?: string;
}

export function endGame(won: boolean, options: EndGameOptions = {}): void {
    const { gameState, renderer, playerProfile } = ctx;
    const roguelikeRunState = ctx.getRoguelikeRunState();
    const roguelikeCurrentNode = ctx.getRoguelikeCurrentNode();

    gameState.gameOver = true;
    gameState.winner = won ? 'player' : 'opponent';

    // Clear session since game is over
    ctx.clearSession();

    // Stop the game timer and calculate duration
    stopGameTimer();
    const durationSeconds = gameStartTime ? Math.floor((Date.now() - gameStartTime) / 1000) : 0;
    const minutes = Math.floor(durationSeconds / 60);
    const seconds = durationSeconds % 60;
    const durationStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;

    playSound(won ? 'victory' : 'defeat');

    // Get faction names for display
    const factions = getFactions();
    const playerFactionData = factions[gameState.faction!];
    const opponentFactionData = factions[gameState.opponentFaction!];

    // Calculate score for SP wins only: a loss earns no score or stars (and
    // posts score 0, so it can never set a leaderboard best).
    let score: number | null = null;
    let starRating: number | null = null;
    if (gameState.mode === 'singleplayer' && won) {
        // Roguelike: use actual node difficulty so scores reflect battle tier
        const difficulty = (gameState.isRoguelike && roguelikeCurrentNode)
            ? roguelikeCurrentNode.difficulty
            : (playerProfile.difficulty || 'knight');
        const scoreMode = gameState.isBlitz ? 'blitz' : 'standard';
        // Time trial still uses standard scoring formula
        score = calculateScore({
            shotsFired: gameState.stats.shotsFired,
            accuracy: gameState.getAccuracy(),
            shipsLost: gameState.stats.shipsLost,
            timeSeconds: durationSeconds,
        }, difficulty, scoreMode);
        starRating = getStarRating(score);
    }

    renderer.showGameOver(won, {
        shotsFired: gameState.stats.shotsFired,
        accuracy: gameState.getAccuracy(),
        shipsLost: gameState.stats.shipsLost,
        turns: turnCount,
        duration: durationStr,
        score,
        starRating,
        isBlitz: gameState.isBlitz,
        isTimeTrial: gameState.isTimeTrial,
        durationSeconds,
    }, {
        mode: gameState.mode,
        playerName: gameState.playerName || 'You',
        playerFactionId: gameState.faction,
        playerFactionName: playerFactionData?.name || gameState.faction,
        opponentName: gameState.opponentName || (gameState.mode === 'singleplayer' ? 'AI' : 'Opponent'),
        opponentFactionId: gameState.opponentFaction,
        opponentFactionName: opponentFactionData?.name || gameState.opponentFaction
    });

    // Show/hide quickplay-specific button
    const newOpponentBtn = document.getElementById('btn-new-opponent');
    if (newOpponentBtn) {
        if (gameState.isQuickplay) {
            newOpponentBtn.classList.remove('hidden');
        } else {
            newOpponentBtn.classList.add('hidden');
        }
    }

    if (options.forfeit && options.forfeitMessage) {
        const message = document.getElementById('gameover-message');
        if (message) message.textContent = options.forfeitMessage;
    }

    // Hide rematch button for tournament matches and forfeits (the room is
    // already gone), show for all other modes
    const rematchAvailable = !gameState.isTournament && !options.forfeit;
    const rematchBtn = document.getElementById('btn-rematch') as HTMLButtonElement | null;
    if (rematchBtn) {
        if (!rematchAvailable) {
            rematchBtn.classList.add('hidden');
        } else {
            rematchBtn.classList.remove('hidden');
            rematchBtn.textContent = 'REMATCH';
            rematchBtn.title = '';
            rematchBtn.disabled = false;
        }
    }

    // Start rematch countdown for MP games
    if (gameState.mode === 'multiplayer' && rematchAvailable) {
        startRematchCountdown(REMATCH_WINDOW_MS);
    }

    // Roguelike campaign: show campaign progress and continue button
    const roguelikeResult = document.getElementById('roguelike-battle-result');
    if (roguelikeResult) {
        if (gameState.isRoguelike && roguelikeRunState) {
            // Record the battle result
            ctx.getRoguelike().recordBattle(roguelikeRunState, won, score || 0,
                roguelikeCurrentNode?.id, {
                    shotsFired: gameState.stats.shotsFired,
                    accuracy: gameState.getAccuracy(),
                    shipsLost: gameState.stats.shipsLost,
                });

            // Capture ship damage for carry-over (respects damageCarryOver config)
            const rlCfg = ctx.getRoguelike().getConfig();
            if (rlCfg.general?.damageCarryOver ?? true) {
                roguelikeRunState.shipDamage = ctx.getRoguelike().captureShipDamage(gameState.ships);
                // Auto-repair surviving ships by 1 cell each after a win
                if (won) {
                    ctx.getRoguelike().autoRepairSurvivingShips(roguelikeRunState);
                }
            } else {
                roguelikeRunState.shipDamage = {};
            }
            ctx.getRoguelike().saveActiveRun(roguelikeRunState);

            // Update campaign progress UI
            const totalTiers = roguelikeRunState.map.length;
            const completed = roguelikeRunState.currentTier + (won ? 1 : 0);
            const progressPct = Math.round((completed / totalTiers) * 100);

            const progressFill = document.getElementById('roguelike-progress-fill');
            const progressText = document.getElementById('roguelike-progress-text');
            const totalScoreEl = document.getElementById('roguelike-total-score');
            const totalWinsEl = document.getElementById('roguelike-total-wins');
            const continueBtn = document.getElementById('btn-roguelike-continue');

            if (progressFill) progressFill.style.width = `${progressPct}%`;
            if (progressText) progressText.textContent = `${completed} / ${totalTiers}`;
            if (totalScoreEl) totalScoreEl.textContent = String(roguelikeRunState.totalScore);
            if (totalWinsEl) totalWinsEl.textContent = String(roguelikeRunState.battlesWon);

            roguelikeResult.classList.remove('hidden');

            // Hide normal game over actions for roguelike
            if (rematchBtn) rematchBtn.classList.add('hidden');
            const mainMenuBtn = document.getElementById('btn-main-menu');
            if (mainMenuBtn) mainMenuBtn.classList.add('hidden');

            if (continueBtn) {
                if (!won && !roguelikeRunState.runActive) {
                    continueBtn.textContent = 'VIEW RESULTS';
                } else {
                    continueBtn.textContent = 'CONTINUE CAMPAIGN';
                }
            }
        } else {
            roguelikeResult.classList.add('hidden');
            // Ensure normal buttons are visible when not in roguelike
            if (rematchBtn && rematchAvailable) rematchBtn.classList.remove('hidden');
            const mainMenuBtn = document.getElementById('btn-main-menu');
            if (mainMenuBtn) mainMenuBtn.classList.remove('hidden');
        }
    }

    // Log single-player match to server
    if (gameState.mode === 'singleplayer') {
        logSinglePlayerMatch(won, score, durationSeconds);

        // Check personal best for time trial wins
        if (gameState.isTimeTrial && won && playerProfile.id) {
            checkTimeTrialPersonalBest(durationSeconds);
        }
    }
}

async function checkTimeTrialPersonalBest(currentTime: number): Promise<void> {
    const { playerProfile } = ctx;
    const label = document.getElementById('time-trial-result-label');
    if (!label) return;

    try {
        const resp = await fetch(`/djbship/api/player/${playerProfile.id}`);
        if (!resp.ok) return;
        const data = await resp.json();
        const difficulty = playerProfile.difficulty || 'knight';
        const ttStat = data.stats?.find(
            (s: { mode: string; difficulty: string; bestTime: number }) =>
                s.mode === 'time-trial' && s.difficulty === difficulty
        );

        // Stats were just updated, so bestTime should reflect the new time if it was better
        // Compare: if bestTime equals currentTime, it means this IS the new best (or first ever)
        if (!ttStat || ttStat.bestTime === 0 || ttStat.bestTime >= currentTime) {
            label.textContent = 'NEW BEST!';
            label.classList.add('new-best');
        } else {
            const mins = Math.floor(ttStat.bestTime / 60);
            const secs = ttStat.bestTime % 60;
            label.textContent = `Best: ${mins}:${secs.toString().padStart(2, '0')}`;
            label.classList.remove('new-best');
        }
    } catch {
        // Silently fail — label already says "TIME TRIAL"
    }
}

// Log single-player stats to server. Match history is logged authoritatively
// server-side at game over (handleGameOver → matchLogger) — the client never
// POSTs /api/matches for SP games.
function logSinglePlayerMatch(playerWon: boolean, score: number | null, durationSeconds: number): void {
    const { gameState, playerProfile } = ctx;

    // TODO(Phase 4): the player-profile stats POST below stays client-side for
    // now — the server's handleGameOver only feeds matchLogger (match
    // history), not /api/player/:id/stats. Move per-profile
    // standard/blitz/time-trial stat recording server-side, then delete this.

    // Update player stats with score. Campaign battles are skipped: the run as
    // a whole is recorded as 'campaign' when it ends (roguelikeUI.ts).
    if (playerProfile.id && !gameState.isRoguelike) {
        // Blitz has no AI moves so difficulty is irrelevant — always store as knight
        const difficulty = gameState.isBlitz ? 'knight' : (playerProfile.difficulty || 'knight');
        fetch(`/djbship/api/player/${playerProfile.id}/stats`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                mode: gameState.isBlitz ? 'blitz' : (gameState.isTimeTrial ? 'time-trial' : 'standard'),
                difficulty,
                won: playerWon,
                score: score || 0,
                turns: gameState.stats.shotsFired,
                timeSeconds: durationSeconds,
                faction: gameState.faction || '',
            })
        }).catch(err => console.error('Failed to update player stats:', err));
    }
}

// Handle ability result from the server
export function handleAbilityResult(result: AbilityResult): void {
    const { gameState, renderer, abilityManager, abilityRenderer } = ctx;

    abilityRenderer.showAbilityResult(result);

    if (!result.success) return;

    // Effect-specific rendering (board overlays/animations/state updates)
    // is routed through the registry in abilityRenderer.ts.
    const env = createAbilityRenderEnv({ gameState, renderer, abilityManager });
    renderOwnAbilityResult(result, env);

    // Process server-sent shot results for damage abilities
    if (result.shotResults) {
        for (const shot of result.shotResults) {
            if (shot.armorAbsorbed) {
                // Roguelike flagship armor absorbed an ability shot's killing
                // blow: the server leaves the cell unrecorded/targetable, so
                // don't store it as a hit here (it would block the finisher).
                renderer.showToast("The flagship's armor absorbed the hit!", true);
                continue;
            }
            if (shot.isDecoy) {
                gameState.enemyShots.set(`${shot.x},${shot.y}`, { hit: false, isDecoy: true });
                if (shot.revealedCells) {
                    for (const c of shot.revealedCells) {
                        gameState.enemyShots.set(`${c.x},${c.y}`, { hit: false, isDecoy: true });
                    }
                }
            } else if (shot.hit) {
                gameState.enemyShots.set(`${shot.x},${shot.y}`, { hit: true });
                renderer.markHit(null, shot.x, shot.y, true);
                if (shot.sunk && shot.sunkShipCells) {
                    renderer.showToast(`Enemy ${shot.shipClass} destroyed!`);
                    for (const cell of shot.sunkShipCells) {
                        gameState.enemyShots.set(`${cell.x},${cell.y}`, { hit: true });
                    }
                    const sunkShipCells = shot.sunkShipCells;
                    const sunkShip = { getCells: () => sunkShipCells };
                    renderer.markSunk(sunkShip, true, { x: shot.x, y: shot.y });
                    // Update opponent ship status
                    if (gameState.opponentShipStatus) {
                        const ship = gameState.opponentShipStatus.find(s => s.class === shot.shipClass && !s.sunk);
                        if (ship) {
                            ship.sunk = true;
                            renderer.renderShipsStatus(
                                gameState.opponentShipStatus.map(s => ({ class: s.class, isSunk: () => s.sunk })),
                                'enemy-ships-status'
                            );
                        }
                    }
                }
            } else {
                gameState.enemyShots.set(`${shot.x},${shot.y}`, { hit: false });
                renderer.markMiss(null, shot.x, shot.y, true);
            }
        }
        renderer.renderEnemyBoard(gameState.enemyShots);
    }

    // Process passive results from ability shots
    renderShotPassives(result.passiveResults, 'own-action', env);

    // Check game-over from ability damage
    if (result.gameOver) {
        endGame(result.winner === gameState.playerId);
        return;
    }

    // Update ability state display
    abilityRenderer.renderAbilities(result.extraShotRemaining || gameState.isMyTurn);
    abilityRenderer.updateBuffs();
}
